import {Flux} from '@nlabs/arkhamjs';
import {getGraphql} from '@nlabs/metropolisjs/utils';

import {
  parseSentinelArticlesResponse,
  publicArticleEndpoint,
  SENTINEL_ARTICLES_QUERY,
  type SentinelArticlesResponse
} from '../services/articles/reaktorArticleClient';
import type {Article} from '../types/article';
import {articlePath} from '../routes/articlePath';

export interface ArticleRepository {
  getByRoute(section: string, slug: string): Promise<Article | undefined>;
}

const normalizeRoutePart = (value: string) => decodeURIComponent(value).trim().toLowerCase();
let latestArkhamState: unknown;

Flux.on('arkhamjs', (state: unknown) => {
  latestArkhamState = state;
});

export const readArkhamArticles = (): Article[] => {
  if(typeof latestArkhamState !== 'object' || latestArkhamState === null || Array.isArray(latestArkhamState)) return [];
  const homepage = (latestArkhamState as Record<string, unknown>).homepage;
  if(typeof homepage !== 'object' || homepage === null || Array.isArray(homepage)) return [];
  const articles = (homepage as Record<string, unknown>).liveArticles;
  return Array.isArray(articles) ? articles as Article[] : [];
};

const loadStoredArticles = async (): Promise<Article[]> => {
  const data = await getGraphql(
    Flux,
    publicArticleEndpoint(window.location.hostname),
    false,
    {
      query: SENTINEL_ARTICLES_QUERY,
      variables: {from: 0, to: 100}
    },
    {}
  ) as NonNullable<SentinelArticlesResponse['data']>;

  const articles = parseSentinelArticlesResponse({data});
  await Flux.dispatch({articles, status: 'live', type: 'ARTICLE_FEED_REPLACE'});
  return articles;
};

export const createArticleRepository = (
  getArticles: () => Promise<Article[]>,
  getCachedArticles: () => Article[] = () => []
): ArticleRepository => ({
  getByRoute: async (section, slug) => {
    const canonicalPath = `/${normalizeRoutePart(section)}/${normalizeRoutePart(slug)}`;
    const findArticle = (articles: Article[]) => articles.find((article) =>
      articlePath(article).toLowerCase() === canonicalPath
    );
    const cachedArticle = findArticle(getCachedArticles());
    return cachedArticle ?? findArticle(await getArticles());
  }
});

export const articleRepository: ArticleRepository = createArticleRepository(loadStoredArticles, readArkhamArticles);
